import { Check, Minus } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LINKS } from "@/i18n/translations";
import { scrollToId } from "@/lib/scroll";
import { Reveal } from "./Reveal";

const Row = ({ label, value }) => (
  <li className="flex items-start justify-between gap-3 py-2.5 border-b lp-hairline last:border-b-0 text-sm">
    <span className="text-[#71717a]">{label}</span>
    <span className="text-right font-medium">
      {value === true ? (
        <Check size={16} className="text-[#f5a623] inline" aria-label="Sí" />
      ) : value === false ? (
        <Minus size={16} className="text-[#3f3f46] inline" aria-label="No" />
      ) : (
        value
      )}
    </span>
  </li>
);

export default function Pricing() {
  const { t } = useLang();
  const L = t.pricing.labels;

  return (
    <section id="precios" className="mx-auto max-w-7xl px-4 sm:px-8 py-20 sm:py-32">
      <Reveal className="text-center">
        <h2 className="font-black tracking-tighter text-3xl sm:text-4xl">{t.pricing.title}</h2>
        <p className="mt-4 text-[#a1a1aa] max-w-2xl mx-auto">{t.pricing.subtitle}</p>
      </Reveal>

      <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch">
        {t.pricing.plans.map((plan, i) => (
          <Reveal key={plan.id} delay={Math.min(i * 0.08, 0.3)} className="h-full">
            <div
              data-testid={`pricing-plan-${plan.id}`}
              className={`relative h-full flex flex-col rounded-2xl border p-6 ${
                plan.featured
                  ? "border-[#f5a623] bg-[#f5a623]/[0.06] shadow-[0_0_60px_-15px_rgba(245,166,35,0.35)]"
                  : "lp-hairline bg-white/[0.02]"
              }`}
            >
              {plan.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#f5a623] text-[#09090b] text-xs font-bold px-3 py-1">
                  {t.pricing.recommended}
                </span>
              )}
              <h3 className="text-lg font-bold tracking-tight">{plan.name}</h3>
              <p className="mt-2">
                <span className="text-4xl font-black tracking-tighter">{plan.price}</span>
                {plan.currency && (
                  <span className="text-sm text-[#a1a1aa] ml-1.5">
                    {plan.currency}
                    {t.pricing.perMonth}
                  </span>
                )}
              </p>
              <ul className="mt-5 flex-1">
                <Row label={L.terminals} value={plan.terminals} />
                <Row label={L.users} value={plan.users} />
                <Row label={L.catalog} value={plan.catalog} />
                <Row label={L.history} value={plan.history} />
                <Row label={L.reports} value={plan.reports} />
                <Row label={L.emailReceipts} value={plan.emailReceipts} />
                <Row label={L.support} value={plan.support} />
              </ul>
              {plan.id === "gratis" ? (
                <a
                  href={LINKS.webPos}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pricing-cta-${plan.id}`}
                  className="mt-6 inline-flex justify-center rounded-full border border-white/20 font-bold px-5 py-2.5 text-sm hover:border-[#f5a623] hover:text-[#f5a623] transition-colors"
                >
                  {t.pricing.ctaFree}
                </a>
              ) : (
                <button
                  onClick={() => scrollToId("lista-espera")}
                  data-testid={`pricing-cta-${plan.id}`}
                  className={`mt-6 inline-flex justify-center rounded-full font-bold px-5 py-2.5 text-sm transition-colors ${
                    plan.featured
                      ? "bg-[#f5a623] text-[#09090b] hover:bg-[#ffbb3d]"
                      : "border border-white/20 hover:border-[#f5a623] hover:text-[#f5a623]"
                  }`}
                >
                  {t.pricing.ctaPaid}
                </button>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-8 text-center space-y-1.5">
        <p className="text-sm font-semibold text-[#f5a623]">{t.pricing.note1}</p>
        <p className="text-sm text-[#a1a1aa]">{t.pricing.note2}</p>
      </Reveal>
    </section>
  );
}
